import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EventService from "../../services/EventService";
import EventShell, { EventError, EventLoading } from "./EventShell";
import {
  audienceLabel,
  capacityLabel,
  formatEventRange,
  formatLabel,
  formatShortDate,
  typeLabel,
} from "./eventFormat";

/** Public event index at /events. */
const EventList = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await EventService.listEvents("all");
      if (!active) return;
      if (result.success) {
        setUpcoming(result.events || []);
        setPast(result.past || []);
      } else {
        setError(result.message || "We couldn't load the event list.");
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <EventShell>
      <span className="ev-eyebrow">KinShield™ Events</span>
      <h1 className="ev-h1">Free sessions on scams and digital safety</h1>
      <p className="ev-lead">
        Practical, plain-language sessions on spotting scams, protecting your
        money, and staying safe online — for older adults, families, community
        groups, and small businesses.
      </p>

      {error && <EventError message={error} />}

      {loading ? (
        <EventLoading label="Loading events…" />
      ) : (
        <>
          <h2 className="ev-h2">Upcoming</h2>
          {upcoming.length === 0 ? (
            <div className="ev-empty">
              <p>No sessions are scheduled right now.</p>
              <Link className="ev-btn ev-btn-primary" to="/events/host-request">
                Invite us to speak at your organization
              </Link>
            </div>
          ) : (
            <div className="ev-grid">
              {upcoming.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <>
              <h2 className="ev-h2">Past sessions</h2>
              <div className="ev-grid">
                {past.slice(0, 6).map((event) => (
                  <EventCard key={event.id} event={event} past />
                ))}
              </div>
            </>
          )}

          <div className="ev-panel ev-panel-accent" style={{ marginTop: 48 }}>
            <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
              Want a session for your group?
            </h2>
            <p className="ev-muted">
              We speak at libraries, senior centers, faith communities, HOAs,
              and workplaces — usually at no cost.
            </p>
            <Link className="ev-btn ev-btn-primary" to="/events/host-request">
              Request a speaker
            </Link>
          </div>
        </>
      )}
    </EventShell>
  );
};

const EventCard = ({ event, past = false }) => {
  const capacity = capacityLabel(event);
  return (
    <Link
      className={`ev-card ${past ? "ev-card-past" : "ev-card-upcoming"}`}
      to={`/events/${event.slug}${past ? "/recap" : ""}`}
    >
      <div className="ev-card-date">{formatShortDate(event.startsAt)}</div>
      <h3 className="ev-card-title">{event.title}</h3>
      <p className="ev-card-summary">{event.summary}</p>
      <div className="ev-badges">
        <span className="ev-badge">{formatLabel(event.format)}</span>
        <span className="ev-badge">{typeLabel(event.eventType)}</span>
        {event.audience && (
          <span className="ev-badge">{audienceLabel(event.audience)}</span>
        )}
      </div>
      <div className="ev-card-footer">
        <span>
          {past ? "View recap" : formatEventRange(event.startsAt, event.endsAt)}
        </span>
        {!past && capacity && (
          <span
            className={`ev-badge ${event.full ? "ev-badge-warn" : "ev-badge-ok"}`}
          >
            {capacity}
          </span>
        )}
      </div>
    </Link>
  );
};

export default EventList;
